/* istanbul ignore file */
import _ from 'lodash'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import configment from '@adc/configment'
import * as graph from '@adc/sdk'
import {
  Button,
  hooks,
  ListingCard,
  useApplicationNotifications,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import { renderHeader } from '../mapHeader'
import ReactGoogleMapApi from '../googleMapApi'
import { ASPECT_RATIO_TOLERANCE } from '../../constants/map'
import {
  getBounds,
  determineBounds,
  fitMarkers,
  shouldMarkersUpdate,
  calculateAspectRatio,
} from '../../util/mapUtils'
import {
  MOBILE_LISTING_CARD,
  trackMapPin,
  trackPropertyClick,
} from '../../services/analytics'
import { isMobile } from '../../util/osDetector'
import { useHoverSubscription, useSetHover } from '../searchPanel/hoverContext'
import cls from './styles.scss'

const cx = classnames.bind(cls)

const MarkerMap = ({
  assets,
  featureFlags,
  filters,
  updateOpenedAsset,
  toggleSaveAsset,
  checkSaved,
  isMapView,
  onFilterChange,
}) => {
  const assetTotal = assets?.total || 0
  const notifications = useApplicationNotifications()
  const [loaded, setLoaded] = useState(false)
  const [aspectRatio, setAspectRatio] = useState(0)
  const [autoMovingMap, setAutoMovingMap] = useState(false)
  const [mapBoundsUpdated, setMapBoundsUpdated] = useState(false)
  const [hasMobileInitialized, setHasMobileInitialized] = useState(false)
  const [selectedMarkerListingData, setSelectedMarkerListingData] =
    useState(null)
  const [isTablet, setIsTablet] = useState(isMobile())
  const [bounds, setBounds] = useState(null)

  const mapRef = useRef()
  const mouseOverEventRef = useRef()
  const mouseOutEventRef = useRef()
  const prevIsMapView = hooks.usePrevious(isMapView, isMapView)
  const prevAssets = hooks.usePrevious(assets, assets)
  const prevFilters = hooks.usePrevious(filters, filters)

  const setHover = useSetHover()

  // TODO:
  const searchMapCoordinates = useCallback(
    mapBounds => {
      onFilterChange({ mapBounds })
    },
    [onFilterChange]
  )

  const updateMapBounds = useCallback(
    (fireNewSearch = true) => {
      setMapBoundsUpdated(false)

      // Just fire new searches when needed.
      // (This function is also used to hide the Redo search Button on new search calls)
      if (fireNewSearch) {
        searchMapCoordinates(bounds)
      }
    },
    [searchMapCoordinates, bounds]
  )

  const fitBounds = useCallback(
    bounds => {
      const currentBounds = mapRef.current.getBounds()
      if (JSON.stringify(bounds) === JSON.stringify(currentBounds)) return
      setAutoMovingMap(true)
      mapRef.current.fitBounds(bounds)
    },
    [mapRef.current] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const initializeMobileMap = useCallback(
    (mapBounds, assetsList) => {
      setHasMobileInitialized(true)
      if (Array.isArray(mapBounds) && mapBounds.length > 1) {
        fitBounds(determineBounds(...mapBounds))
      } else {
        fitBounds(fitMarkers(assetsList))
      }
    },
    [fitBounds]
  )

  const onBoundsChanged = useCallback(
    (...args) => {
      if (!mapRef?.current) return
      setBounds(getBounds(mapRef.current.getBounds()))

      if (autoMovingMap) {
        setAutoMovingMap(false)
      } else {
        // If the actual and new aspect ratios are close enough, no new search is needed
        const mapDiv = mapRef.current.getDiv()
        const newAspectRatio = calculateAspectRatio(
          mapDiv.offsetWidth,
          mapDiv.offsetHeight
        )
        const hasAspectRatioChanged = aspectRatio !== newAspectRatio
        const hasAspectRatioExceededTolerance =
          Math.abs(aspectRatio - newAspectRatio) > ASPECT_RATIO_TOLERANCE
        const shouldUpdateMap =
          !hasAspectRatioChanged || hasAspectRatioExceededTolerance
        setAspectRatio(newAspectRatio)

        if (shouldUpdateMap) {
          setMapBoundsUpdated(true)
        }

        setIsTablet(isMobile())
      }
    },
    [autoMovingMap, mapRef, aspectRatio, setIsTablet]
  )

  const onGoogleMapsLoad = useCallback(
    mapInfo => {
      const map = mapInfo?.map
      if (!map || !window.google) return
      mapRef.current = map

      if (!map && loaded) setLoaded(false)

      if (map && !loaded) {
        const mapDiv = map.getDiv()
        const aspectRatio = calculateAspectRatio(
          mapDiv.offsetWidth,
          mapDiv.offsetHeight
        )
        // Only mark the map ready here. The initial fit is a readiness-gated
        // one-shot effect (see below) so it can't run before the search bounds
        // / markers are available.
        setLoaded(true)
        setAspectRatio(aspectRatio)
      }
    },
    [mapRef.current, loaded] // eslint-disable-line react-hooks/exhaustive-deps
  )

  hooks.useMountEffect(() => {
    mouseOverEventRef.current = new MouseEvent('mouseover', {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: 0,
    })
    mouseOutEventRef.current = new MouseEvent('mouseout', {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: 0,
    })
  })
  const handleListingHover = useCallback(
    ({ id, source }) => {
      // Only track hover events from map pins, not from property cards
      if (source !== 'map') return

      const listing = assets.list.find(item => item?.listing_id === id)

      // only trigger hover event track map pin for non mobile environments
      if (!isMobile() && listing) {
        trackMapPin({
          listing,
          isMobileView: false,
          featureFlags,
          isHoverEvent: true,
        })
      }
    },
    [assets, featureFlags]
  )

  useHoverSubscription(handleListingHover)

  // Initial auto-fit: run exactly once, when the map is loaded AND the first
  // search has resolved (so mapBounds / markers are available). This replaces
  // the fit that used to live in onGoogleMapsLoad, which could fire before the
  // search data was ready. One-shot by design — after the initial fit the map
  // viewport is the user's (panning must not be overridden).
  const didInitialFitRef = useRef(false)
  useEffect(() => {
    if (didInitialFitRef.current || !loaded || !window.google) return
    if (assets.fetchProgress.loading) return

    const { mapBounds } = filters
    const bounds =
      Array.isArray(mapBounds) && mapBounds.length > 1
        ? determineBounds(...mapBounds)
        : fitMarkers(assets.list)

    fitBounds(bounds)
    didInitialFitRef.current = true
  }, [loaded, assets, filters, fitBounds])

  useEffect(() => {
    const isNewSearch =
      assets?.fetchProgress.loading &&
      prevAssets.fetchProgress.loading !== assets?.fetchProgress.loading
    if (isNewSearch) updateMapBounds(false)
  }, [assets, prevAssets, updateMapBounds])

  useEffect(() => {
    const dataLoaded = loaded && !assets.fetchProgress.loading
    if (dataLoaded && window.google) {
      const { mapBounds } = filters
      const nextMarkersList = assets.list
      const currentMarkersList = prevAssets.list

      // Only runs when client first lands on the map screen - which happens from asset(mobile list) view currently.
      if (!hasMobileInitialized && isMapView !== prevIsMapView && isMapView) {
        initializeMobileMap(mapBounds, nextMarkersList)
        return
      }
      const mapViewHasBeenEnabled = isMapView && !prevIsMapView

      // The map will move and autosize based on locations of markers
      const markersHaveChanged = shouldMarkersUpdate(
        currentMarkersList,
        nextMarkersList,
        mapBounds
      )
      if (markersHaveChanged || mapViewHasBeenEnabled) {
        fitBounds(fitMarkers(nextMarkersList))
      }
    }
  }, [
    filters,
    hasMobileInitialized,
    initializeMobileMap,
    isMapView,
    loaded,
    assets,
    prevAssets,
    prevIsMapView,
    fitBounds,
  ])

  const { list: markers, fetchProgress } = assets

  useEffect(() => {
    const getOpenedAssetForMobile = async () => {
      if (
        filters.openedAssetId &&
        filters.openedAssetId !== prevFilters.openedAssetId
      ) {
        /**
         * Note: openedAssetID and selectedAssetId are basically one in the same at the moment,
         * where openedAssetID is used to open the mobile card and
         * selectedAssetId is for highlighting the pin (showing popup)
         * May want to consider consolidating them into one at some point.
         */
        setHover(filters.openedAssetId, 'map')

        /**
         * TODO:
         * This is admittedly ugly as all get out...
         * We need to consolidate to a single data format irrespective of how the data is being loaded.
         * My only concern is where else we are referring to markers depending if flag on or off.
         */
        let existingMarkerListingData = null
        existingMarkerListingData =
          _.find(markers, o => o.listing_id === filters.openedAssetId) || null
        setSelectedMarkerListingData(existingMarkerListingData)
      }
    }

    if (isTablet) {
      getOpenedAssetForMobile()
    }
  }, [
    setHover,
    filters.openedAssetId,
    prevFilters.openedAssetId,
    markers,
    assets,
    isTablet,
    featureFlags,
  ])

  useEffect(() => {
    renderHeader(mapRef.current, markers.length, assetTotal, featureFlags)
    // Note on deps, eslint wants me to use mapRef but it works better watching mapRef.current
  }, [markers, assetTotal, mapRef.current, featureFlags]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectedMarkerData = useCallback(
    listingId => {
      const marker = document.getElementById(listingId)
      marker?.dispatchEvent(mouseOverEventRef.current)
      if (
        listingId &&
        selectedMarkerListingData?.listing_id &&
        listingId === selectedMarkerListingData?.listing_id
      ) {
        setSelectedMarkerListingData(null)
        updateOpenedAsset(null)
        marker?.dispatchEvent(mouseOutEventRef.current)
      }
    },
    [selectedMarkerListingData, updateOpenedAsset]
  )

  const handleClickIfNotCancelled = (listingData = {}) => {
    if (
      selectedMarkerListingData.listing_status_group !==
      graph.enums.ListingStatusGroup.Canceled
    ) {
      trackPropertyClick({
        listing: selectedMarkerListingData,
        triggeredFrom: MOBILE_LISTING_CARD,
        ...listingData,
      })
    }
  }

  const handleMapClick = useCallback(() => {
    setHover(0, 'map')
    setSelectedMarkerListingData(null)
    updateOpenedAsset(null)
  }, [setHover, updateOpenedAsset, setSelectedMarkerListingData])

  const handleSaveIfNotCancelled = (_event, analyticsData = {}) => {
    if (
      selectedMarkerListingData.listing_status_group !==
      graph.enums.ListingStatusGroup.Canceled
    ) {
      const { badges, isReserveMetShown, displayedCurrentBid, displayedERV } =
        analyticsData
      toggleSaveAsset({
        listing: selectedMarkerListingData,
        updateListingSavedCallback: value => {
          setSelectedMarkerListingData({
            ...selectedMarkerListingData,
            primary_property: {
              ...selectedMarkerListingData.primary_property,
              is_currently_saved: value,
            },
          })
        },
        badges,
        isReserveMetShown,
        displayedCurrentBid,
        displayedERV,
        notifications,
      })
    }
  }

  // Only display the assets while they're part of the active search
  const assetsLoaded = !fetchProgress.loading

  // Avoid displaying the map before the initial load is done
  const showMap = assetsLoaded && loaded

  return (
    <div className={cx('full-map-container')} data-elm-id='Asset_Map_View'>
      <div className={cx('map-container')}>
        <div
          className={cx(
            'map-action-buttons-root',
            selectedMarkerListingData && 'with-mobile-marker'
          )}
        >
          {mapBoundsUpdated && (
            <Button
              role='secondary'
              className={cx('redo-search-button')}
              elmId='map_redo_search_button'
              onClick={updateMapBounds}
            >
              Search this Area!
            </Button>
          )}
        </div>
        <ReactGoogleMapApi
          featureFlags={featureFlags}
          channel={configment.get('GOOGLE_MAPS_CHANNEL_NAME')}
          googleMapsApiKey={configment.get('GOOGLE_MAPS_API_KEY')}
          mapId={configment.get('GOOGLE_MAPS_MAP_ID', 'DEMO_MAP_ID')}
          onBoundsChanged={onBoundsChanged}
          bounds={bounds}
          onMarkerClick={listingId => {
            updateOpenedAsset(listingId)
            handleSelectedMarkerData(listingId)
          }}
          assetsLoaded={assetsLoaded}
          showMap={showMap}
          markers={markers}
          onGoogleMapsLoad={onGoogleMapsLoad}
          assetTotal={assetTotal}
          map={mapRef?.current}
          onMapClick={handleMapClick}
        />
      </div>
      {selectedMarkerListingData && isTablet && (
        <div className={cx('mobile-card-container')}>
          <ListingCard
            listing={selectedMarkerListingData}
            elmId={`listing_card_${selectedMarkerListingData.listing_id}`}
            linkProps={{
              onClick: handleClickIfNotCancelled,
              href: selectedMarkerListingData.listing_page_path,
            }}
            showV2
            showSave
            onSaveToggle={handleSaveIfNotCancelled}
            isSaved={checkSaved(selectedMarkerListingData)}
            viewMode='horizontal'
            isCancelled={
              selectedMarkerListingData.listing_status ===
              graph.enums.ListingStatusGroup.Canceled
            }
            featureFlags={featureFlags}
          />
        </div>
      )}
    </div>
  )
}

MarkerMap.propTypes = {
  assets: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  updateOpenedAsset: PropTypes.func.isRequired,
  toggleSaveAsset: PropTypes.func.isRequired,
  checkSaved: PropTypes.func.isRequired,
  isMapView: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
}

// Memoized: markers don't depend on saved-state, so the map must not re-render
// on every save/unsave (rebuilding up to MAX_ALLOWED_PINS markers). Props are
// kept referentially stable in searchPanel (stable handlers, stable checkSaved).
export default React.memo(MarkerMap)
